import React, { useEffect, useState } from 'react';
import { getWeeklySummary, WeeklySummaryResponse } from '../../services/api';

interface ThisWeekCardProps {
  studentId: string;
  recentActivity: { date: string; questions: number; correct: number }[];
  streakDays: number;
}

export const ThisWeekCard: React.FC<ThisWeekCardProps> = ({
  studentId,
  recentActivity,
  streakDays,
}) => {
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [summary, setSummary] = useState<WeeklySummaryResponse | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Generate last 7 days
  const days: { date: string; questions: number; correct: number }[] = [];
  const today = new Date();

  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    const activity = recentActivity.find(a => a.date === dateStr);
    days.push({
      date: dateStr,
      questions: activity?.questions || 0,
      correct: activity?.correct || 0,
    });
  }

  // Calculate week totals
  const weekQuestions = days.reduce((sum, d) => sum + d.questions, 0);
  const weekCorrect = days.reduce((sum, d) => sum + d.correct, 0);
  const weekAccuracy = weekQuestions > 0 ? Math.round((weekCorrect / weekQuestions) * 100) : 0;
  const daysActive = days.filter(d => d.questions > 0).length;

  const getIntensity = (questions: number) => {
    if (questions === 0) return 0;
    if (questions >= 20) return 4;
    if (questions >= 10) return 3;
    if (questions >= 5) return 2;
    return 1;
  };

  const loadSummary = async () => {
    if (summary) {
      setSummaryExpanded(!summaryExpanded);
      return;
    }

    setLoadingSummary(true);
    try {
      const data = await getWeeklySummary(studentId, false);
      setSummary(data);
      setSummaryExpanded(true);
    } catch (err) {
      console.error('Failed to load summary:', err);
    } finally {
      setLoadingSummary(false);
    }
  };

  return (
    <div className="this-week-card">
      <div className="this-week-header">
        <h3>This Week</h3>
        <div className="streak-badge">
          <span className="streak-fire">&#128293;</span>
          <span className="streak-count">{streakDays} day{streakDays !== 1 ? 's' : ''}</span>
        </div>
      </div>

      <div className="week-activity-grid">
        {days.map(({ date, questions }) => {
          const dayDate = new Date(date + 'T00:00:00');
          const dayLabel = dayDate.toLocaleDateString('en-US', { weekday: 'narrow' });
          const isToday = date === today.toISOString().split('T')[0];

          return (
            <div
              key={date}
              className={`week-day intensity-${getIntensity(questions)} ${isToday ? 'today' : ''}`}
              title={`${dayDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}: ${questions} questions`}
            >
              <span className="day-label">{dayLabel}</span>
              <div className="day-dot" />
            </div>
          );
        })}
      </div>

      <div className="week-stats">
        <div className="week-stat">
          <span className="stat-value">{weekQuestions}</span>
          <span className="stat-label">questions</span>
        </div>
        <div className="week-stat">
          <span className="stat-value">{weekAccuracy}%</span>
          <span className="stat-label">accuracy</span>
        </div>
        <div className="week-stat">
          <span className="stat-value">{daysActive}/7</span>
          <span className="stat-label">days active</span>
        </div>
      </div>

      <button
        className="summary-toggle"
        onClick={loadSummary}
        disabled={loadingSummary}
      >
        {loadingSummary ? 'Loading...' : summaryExpanded ? 'Hide Summary' : 'View Full Summary'}
      </button>

      {summaryExpanded && summary && (
        <div className="week-summary-content">
          {summary.summary.split('\n\n').map((paragraph, idx) => (
            <p key={idx}>{paragraph}</p>
          ))}

          {summary.data.improved_los.length > 0 && (
            <div className="summary-highlights">
              <span className="highlight-label">Improved:</span>
              {summary.data.improved_los.slice(0, 2).map((lo, idx) => (
                <span key={idx} className="highlight-item positive">
                  {lo.description || lo.lo} +{Math.round(lo.change)}%
                </span>
              ))}
            </div>
          )}

          {summary.data.declined_los.length > 0 && (
            <div className="summary-focus">
              <span className="focus-label">Review:</span>
              {summary.data.declined_los.slice(0, 2).map((lo, idx) => (
                <span key={idx} className="focus-item">
                  {lo.description || lo.lo}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ThisWeekCard;
